import { CLOSING_BRACKETS, OPENING_BRACKETS } from "shared/constants";

// known acronyms that should always be uppercased
const KNOWN_ACRONYMS = ["frc", "vin", "id", "api", "url", "qls"];

/**
 * We want this to be able to handle camelCase, snake_case, and mixedCase + numbers, ie.
 * Initially this comes from https://stackoverflow.com/questions/196972/convert-string-to-title-case-with-javascript,
 * but is extended.
 *
 * "someText" -> "Some Text"
 * "some_text" -> "Some Text"
 * "someText123" -> "Some Text 123"
 * "ALLCAPS" -> "ALLCAPS"
 * "ALLCaps" -> "ALLCaps"
 * "ALLcaps" -> "ALLcaps"
 * "mixedALLCAPS" -> "Mixed ALLCAPS"
 * "snake_case_WITH_CAPS" -> "Snake Case WITH CAPS"
 * "Randomword" -> "Randomword"
 * "mixedCase5Word" -> "Mixed Case 5 Word"
 * "frc_metrics" -> "FRC Metrics"
 */
export const toTitleCase = (str: string): string => {
  // Split the string at digit boundaries, spaces, underscores, hyphens, and camelCase boundaries
  const parts = str.split(
    /(?<=[a-z])(?=[A-Z])|(?<=\D)(?=\d)|(?<=\d)(?=\D)|[\s_-]+/
  );

  const toTitle = (str: string) => {
    // Check if the word is a known acronym
    if (KNOWN_ACRONYMS.includes(str.toLowerCase())) {
      return str.toUpperCase();
    }

    // Check if the word is in ALLCaps format (uppercase letters followed by lowercase)
    if (/^[A-Z]+[a-z]*$/.test(str)) {
      return str; // Keep it as is
    }

    // Check if the word is all uppercase
    if (str === str.toUpperCase()) {
      return str; // Keep it as is
    }

    // Otherwise, capitalize the first letter and lowercase the rest
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  // Apply toTitle to each part and join with spaces
  return parts.map(toTitle).join(" ");
};

export const slugify = (value: string, separator = "-") =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, separator)
    .replace(new RegExp(`^${separator}|${separator}$`, "g"), "");

// https://www.30secondsofcode.org/js/s/to-snake-case
export const toSnakeCase = (str: string) =>
  str
    .match(
      /[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g
    )!
    ?.map((x) => x.toLowerCase())
    ?.join("_");

// https://stackoverflow.com/questions/2970525/converting-any-string-into-camel-case
export const toCamelCase = (str: string) =>
  str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
      index === 0 ? word.toLowerCase() : word.toUpperCase()
    )
    .replace(/\s+/g, "");

export const capitalizeFirstLetter = (input: string) =>
  input.charAt(0).toUpperCase() + input.slice(1);

export const wrapInQuotesAndJoin = (
  list: string[] | number[],
  separator = "|"
): string => list.map((filter) => `"${filter}"`).join(separator);

export const splitByFirstOccurrence = (value: string, separator: string) => {
  const [first, ...rest] = value.split(separator);
  const remainder = rest.join(separator);

  return [first, remainder];
};

/**
 * Splits a string by a delimiter while ignoring the delimiter
 * - if it is surrounded by double-quotes
 * - if it is surrounded by brackets (parentheses, curly braces or square brackets)
 */
export const splitIgnoringDoubleQuotes = (
  str: string,
  delimiter: string
): string[] => {
  const result = [];
  let insideQuotes = false;
  let insideBrackets = 0;
  let substringStart = 0;
  let isEscaped = false;

  for (let i = 0; i < str.length; i++) {
    const char = str[i];

    if (char === "\\" && !isEscaped) {
      isEscaped = true;
      continue;
    }

    if (char === '"' && !isEscaped) {
      insideQuotes = !insideQuotes;
    } else if (!insideQuotes && OPENING_BRACKETS.includes(char)) {
      insideBrackets++;
    } else if (!insideQuotes && CLOSING_BRACKETS.includes(char)) {
      insideBrackets--;
    }

    if (
      !insideQuotes &&
      insideBrackets === 0 &&
      str.substring(i, i + delimiter.length) === delimiter
    ) {
      result.push(str.substring(substringStart, i));
      substringStart = i + delimiter.length;
      i += delimiter.length - 1;
    }

    isEscaped = false;
  }

  result.push(str.substring(substringStart));

  return result.filter(Boolean);
};

export const getAttributeLabel = (attributeID: string) =>
  toTitleCase(toSnakeCase(attributeID));

export const removeNonDigitCharacters = (str: string): string =>
  str.replace(/\D/g, "");

// Examples:
// "00123" → "123"
// "0" → "0" (preserves single zero)
// "00.5" → "0.5" (preserves zero before decimal)
// "000" → "0" (preserves one zero)
// "00abc" → "00abc" (non-numeric strings are preserved)
export const removeLeadingZeros = (str: string): string => {
  // Handle empty string case
  if (!str) return str;

  // If it's a purely numeric string, remove leading zeros but keep a single zero
  if (/^\d+$/.test(str)) {
    return str.replace(/^0+(?=\d)/, "");
  }

  // If it's a numeric string with decimal, ensure we keep zero before the decimal
  if (/^\d+\.\d+$/.test(str)) {
    return str.replace(/^0+(?=\d)/, "");
  }

  // Otherwise it has non-numeric characters, so we return it as is
  return str;
};

export const cleanString = (str: string) =>
  str
    .replace(/[^a-zA-Z0-9]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const pluralize = (
  word: string,
  count?: number,
  pluralForm?: string
) => {
  if (pluralForm) {
    return `${count === 1 ? word : pluralForm}`;
  }

  if (count === 1) {
    return word;
  }

  if (word.endsWith("y") && !/[aeiou]y$/i.test(word)) {
    return `${word.slice(0, -1)}ies`;
  }

  // A sibilant ending takes -es: a status reads as "statuses", a batch as "batches".
  if (/(s|x|z|ch|sh)$/i.test(word)) {
    return `${word}es`;
  }

  return `${word}s`;
};

export const joinWithCommasAndAnd = (items: string[]) => {
  if (items.length === 0) return "";

  if (items.length === 1) return items[0];

  return items.slice(0, -1).join(", ") + " and " + items.slice(-1);
};
